import React, { useState, useEffect, useCallback, useRef } from 'react';

if (typeof window !== 'undefined') window.React = React;

const ImageAnnotator = ({ imageUrl, existingAnnotations, onSave, onClose }) => {
  const [Editor, setEditor] = useState(null);
  const [constants, setConstants] = useState(null);
  const [designState, setDesignState] = useState(null);
  // The editor populates this ref with a function that exports the edited image
  // at the SOURCE image's natural pixel dimensions (annotations composited at the
  // matching scale, crop/rotation applied) — not the letterboxed on-screen canvas.
  const getImgDataRef = useRef(null);

  useEffect(() => {
    import('react-filerobot-image-editor').then((mod) => {
      setEditor(() => mod.default);
      setConstants({ TABS: mod.TABS, TOOLS: mod.TOOLS });
    });
  }, []);

  const handleApply = useCallback(() => {
    const getImgData = getImgDataRef.current;
    if (getImgData) {
      // pixelRatio = 1 -> output matches the source image's natural pixel
      // dimensions, so the saved markup has the same size as the original.
      const { imageData } = getImgData({}, 1) || {};
      if (imageData?.imageBase64) {
        onSave(imageData.imageBase64);
        return;
      }
    }
    onClose();
  }, [onSave, onClose]);

  if (!Editor || !constants) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100001,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: '#fff', fontSize: 16 }}>Loading editor...</div>
      </div>
    );
  }

  const { TABS, TOOLS } = constants;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100001,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleApply}
          style={{
            padding: '8px 24px',
            borderRadius: 8,
            border: 'none',
            background: '#45B7AA',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Save Markup
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '8px 20px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
      <div style={{ width: '92vw', height: '85vh', borderRadius: 12, overflow: 'hidden' }}>
        <Editor
          source={imageUrl}
          getCurrentImgDataFnRef={getImgDataRef}
          onModify={(state) => setDesignState(state)}
          onClose={onClose}
          removeSaveButton
          tabsIds={[TABS.ANNOTATE]}
          defaultTabId={TABS.ANNOTATE}
          defaultToolId={TOOLS.ARROW}
          annotationsCommon={{ fill: 'rgba(255,0,0,0.3)', stroke: '#dc2626', strokeWidth: 2 }}
          Arrow={{ stroke: '#dc2626', strokeWidth: 3 }}
          Rect={{ stroke: '#dc2626', strokeWidth: 2, fill: 'transparent', cornerRadius: 4 }}
          Ellipse={{ stroke: '#dc2626', strokeWidth: 2, fill: 'transparent' }}
          Text={{
            text: 'Note',
            fontSize: 18,
            fontFamily: 'Arial',
            fill: '#dc2626',
            fonts: [
              { label: 'Arial', value: 'Arial' },
              { label: 'Helvetica', value: 'Helvetica' },
              { label: 'Courier', value: 'Courier New' },
            ],
          }}
          Pen={{ stroke: '#dc2626', strokeWidth: 3 }}
          Line={{ stroke: '#dc2626', strokeWidth: 2 }}
          defaultSavedImageType="png"
          defaultSavedImageQuality={0.92}
          savingPixelRatio={1}
          previewPixelRatio={window.devicePixelRatio || 2}
        />
      </div>
    </div>
  );
};

export default ImageAnnotator;
